import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import PageLayout from "../components/layout/PageLayout";
import { LoadingOverlay } from "../components/ui/loading-overlay";
import { fetchWithAuth } from "../Context/UserContext";
import API_URL from "../config";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";

const CANALES = [
  { value: "", label: "Todos los canales" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "widget", label: "Widget web" },
];

const FUNNEL_STAGES = [
  { value: "", label: "Todas las etapas" },
  { value: "nuevo", label: "Nuevo" },
  { value: "explorando", label: "Explorando" },
  { value: "interesado", label: "Interesado" },
  { value: "registrado", label: "Registrado" },
  { value: "activado", label: "Activado" },
];

const StaffChatDashboard = () => {
  const [conversaciones, setConversaciones] = useState([]);
  const [resumenQuejas, setResumenQuejas] = useState(null);
  const [resumenFunnel, setResumenFunnel] = useState(null);
  const [seleccionada, setSeleccionada] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [canal, setCanal] = useState("");
  const [soloQuejas, setSoloQuejas] = useState(false);
  const [funnelStage, setFunnelStage] = useState("");

  const cargarConversaciones = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (canal) params.set("channel", canal);
      if (soloQuejas) params.set("flag_queja", "true");
      if (funnelStage) params.set("funnel_stage", funnelStage);

      const response = await fetchWithAuth(`${API_URL}/bs/staff/conversaciones?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setConversaciones(data.conversaciones);
      } else {
        const data = await response.json();
        setError(data.error || "No tienes acceso a esta sección.");
      }
    } catch (err) {
      console.error("Error al cargar conversaciones:", err);
      setError("Error en la conexión con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  const cargarResumenQuejas = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/bs/staff/conversaciones/quejas-resumen`);
      if (response.ok) {
        setResumenQuejas(await response.json());
      }
    } catch (err) {
      console.error("Error al cargar el resumen de quejas:", err);
    }
  };

  const cargarResumenFunnel = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/bs/staff/conversaciones/funnel-resumen`);
      if (response.ok) {
        setResumenFunnel(await response.json());
      }
    } catch (err) {
      console.error("Error al cargar el resumen del funnel:", err);
    }
  };

  useEffect(() => {
    cargarConversaciones();
    cargarResumenQuejas();
    cargarResumenFunnel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canal, soloQuejas, funnelStage]);

  const verDetalle = async (conversacion) => {
    setSeleccionada(conversacion);
    setMensajes([]);
    try {
      const response = await fetchWithAuth(`${API_URL}/bs/staff/conversaciones/${conversacion.id}`);
      if (response.ok) {
        const data = await response.json();
        setMensajes(data.mensajes);
      }
    } catch (err) {
      console.error("Error al cargar el detalle de la conversación:", err);
    }
  };

  if (loading && conversaciones.length === 0 && !error) {
    return <LoadingOverlay />;
  }

  return (
    <PageLayout>
      <Helmet>
        <title>Conversaciones - Procu</title>
      </Helmet>

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <h1 className="text-2xl font-semibold text-foreground">Conversaciones del bot</h1>
        <p className="text-sm text-muted-foreground">Solo visible para el equipo de Procu.</p>

        {error && <p className="mt-6 text-sm text-destructive">{error}</p>}

        {resumenFunnel && (
          <div className="mt-6">
            <h2 className="text-sm font-semibold text-foreground">Funnel de captación</h2>
            <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-5">
              {resumenFunnel.por_stage.map((item) => (
                <Card key={item.stage}>
                  <CardContent className="pt-6">
                    <p className="text-sm capitalize text-muted-foreground">{item.stage}</p>
                    <p className="mt-1 text-2xl font-semibold text-foreground">{item.cantidad}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            {(resumenFunnel.por_intencion.comprador || resumenFunnel.por_intencion.proveedor) && (
              <p className="mt-2 text-xs text-muted-foreground">
                Por intención: {resumenFunnel.por_intencion.comprador || 0} comprador(es),{" "}
                {resumenFunnel.por_intencion.proveedor || 0} proveedor(es)
              </p>
            )}
          </div>
        )}

        {resumenQuejas && (
          <div className="mt-6">
            <h2 className="text-sm font-semibold text-foreground">Quejas recurrentes por empresa</h2>
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Total de quejas</p>
                  <p className="mt-1 text-2xl font-semibold text-foreground">{resumenQuejas.total_quejas}</p>
                </CardContent>
              </Card>
              {resumenQuejas.por_empresa.map((item) => (
                <Card key={item.nombre_empresa}>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">{item.nombre_empresa}</p>
                    <p className="mt-1 text-2xl font-semibold text-foreground">{item.cantidad_quejas}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {CANALES.map((c) => (
            <Button
              key={c.value}
              variant={canal === c.value ? "default" : "outline"}
              size="sm"
              onClick={() => setCanal(c.value)}
            >
              {c.label}
            </Button>
          ))}
          <Button
            variant={soloQuejas ? "default" : "outline"}
            size="sm"
            onClick={() => setSoloQuejas((prev) => !prev)}
          >
            Solo quejas
          </Button>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {FUNNEL_STAGES.map((stage) => (
            <Button
              key={stage.value}
              variant={funnelStage === stage.value ? "default" : "outline"}
              size="sm"
              onClick={() => setFunnelStage(stage.value)}
            >
              {stage.label}
            </Button>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-2">
            {conversaciones.length === 0 && (
              <p className="text-sm text-muted-foreground">No hay conversaciones con estos filtros.</p>
            )}
            {conversaciones.map((conversacion) => (
              <Card
                key={conversacion.id}
                className="cursor-pointer hover:bg-muted"
                onClick={() => verDetalle(conversacion)}
              >
                <CardContent className="flex items-center justify-between gap-4 pt-6">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {conversacion.nombre_empresa || conversacion.nombre_usuario || conversacion.phone_number || "Anónimo"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {conversacion.channel} · {new Date(conversacion.last_message_at).toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {(conversacion.tags || []).join(", ") || "sin tags"}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="rounded-full bg-primary/10 px-2 py-1 text-xs font-medium capitalize text-primary">
                      {conversacion.funnel_stage}
                      {conversacion.intencion ? ` · ${conversacion.intencion}` : ""}
                    </span>
                    {conversacion.flag_queja && (
                      <span className="rounded-full bg-destructive/10 px-2 py-1 text-xs font-medium text-destructive">
                        Queja
                      </span>
                    )}
                    {conversacion.status === "closed" && (
                      <span className="rounded-full bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
                        Cerrada
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div>
            {seleccionada ? (
              <Card>
                <CardContent className="max-h-[32rem] space-y-2 overflow-y-auto pt-6">
                  <p className="mb-2 text-sm font-semibold text-foreground">
                    Conversación #{seleccionada.id}
                  </p>
                  {mensajes.map((mensaje) => (
                    <div key={mensaje.id} className="text-sm">
                      <span className="font-medium text-foreground">
                        {mensaje.sender_type === "bot" ? "Bot" : mensaje.sender_type === "staff" ? "Staff" : "Usuario"}:
                      </span>{" "}
                      <span className="text-muted-foreground">{mensaje.content}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <p className="text-sm text-muted-foreground">
                Selecciona una conversación para ver el hilo completo.
              </p>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default StaffChatDashboard;
